import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import StarRating from '../components/StarRating';
import LoadingSpinner from '../components/LoadingSpinner';
import BadgeDisplay from '../components/BadgeDisplay';
import ReferralShare from '../components/ReferralShare';
import { clearAppCache, APP_VERSION } from '../lib/storage';
import {
  User,
  Edit2,
  Save,
  Star,
  MapPin,
  Calendar,
  LogOut,
  X,
  Mail,
  Lock,
  Settings,
  Bookmark,
  ChevronRight,
  Camera,
  Trash2,
  AlertTriangle,
  Wrench,
  RefreshCw,
  Eye,
  EyeOff,
  Shield
} from 'lucide-react';

function Profile() {
  const navigate = useNavigate();
  const { user, profile, loading: authLoading, updateProfile, changeEmail, signOut, uploadProfilePicture, deleteAccount } = useAuth();
  const { ratings, comments, bars, friends, followers, following, lists, loading: appLoading } = useApp();
  const fileInputRef = useRef(null);

  const [isEditing, setIsEditing] = useState(false);
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [isPrivate, setIsPrivate] = useState(profile?.profile_privacy === 'private');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  
  // Email change form
  const [emailForm, setEmailForm] = useState({
    newEmail: '',
    password: ''
  });

  if (authLoading || appLoading) {
    return <LoadingSpinner />;
  }

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-dive-black py-6 px-4">
        <div className="max-w-2xl mx-auto text-center py-12">
          <img src="/logo-icon.png" alt="" className="w-16 h-16 object-contain mx-auto mb-4 opacity-50" />
          <h2 className="text-white text-xl mb-2">Join the Community</h2>
          <p className="text-gray-400 text-sm mb-6">
            Sign in to rate bars, add comments, and connect with other dive bar enthusiasts
          </p>
          <Link to="/auth" className="btn-primary inline-flex items-center gap-2">
            Sign In / Sign Up
          </Link>
        </div>
      </div>
    );
  }

  const userRatings = ratings.filter(r => r.userId === user.id);
  const userComments = comments.filter(c => c.userId === user.id);
  const wantToTryList = lists.find(l => l.is_system && l.title === 'Want to Try');
  const wantToTryCount = wantToTryList?.list_bars?.length || 0;

  const handleSave = async () => {
    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      await updateProfile({
        display_name: displayName.trim(),
        profile_privacy: isPrivate ? 'private' : 'public'
      });
      setIsEditing(false);
      setSuccess('Profile updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      await changeEmail(emailForm.newEmail, emailForm.password);
      setShowEmailChange(false);
      setEmailForm({ newEmail: '', password: '' });
      setSuccess('Verification email sent! Check your inbox to confirm the change.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error changing email:', err);
      setError(err.message || 'Failed to change email');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setError('');

    try {
      await uploadProfilePicture(file);
      setSuccess('Profile picture updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      setError(err.message || 'Failed to upload profile picture');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError('Please enter your password');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    try {
      await deleteAccount(deletePassword);
      navigate('/');
    } catch (err) {
      console.error('Error deleting account:', err);
      setDeleteError(err.message || 'Failed to delete account');
    } finally {
      setIsDeleting(false);
    }
  };

  // Get the user's rated bars
  const ratedBars = userRatings.map(rating => {
    const bar = bars.find(b => b.id === rating.barId);
    return { ...rating, bar };
  }).filter(r => r.bar);

  return (
    <div className="min-h-screen bg-dive-black py-6 pb-24">
      <div className="max-w-2xl mx-auto px-4">
        {/* Success Message */}
        {success && (
          <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-lg mb-4 text-sm">
            {success}
          </div>
        )}

        {/* Profile Card */}
        <div className="bg-dive-dark border border-dive-gray rounded-xl overflow-hidden mb-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-dive-neon/30 to-dive-amber/20 p-6">
            <div className="flex items-center gap-4">
              {/* Profile Picture */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleProfilePictureChange}
                className="hidden"
              />
              <button
                onClick={handleProfilePictureClick}
                disabled={isUploading}
                className="relative w-20 h-20 rounded-full overflow-hidden border-2 border-dive-neon group"
              >
                {profile?.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt={profile?.display_name || 'Profile'}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                  />
                ) : null}
                <div
                  className="w-full h-full bg-dive-darker items-center justify-center"
                  style={{ display: profile?.avatar_url ? 'none' : 'flex' }}
                >
                  <span className="text-3xl font-display text-dive-neon">
                    {(profile?.display_name || user?.user_metadata?.display_name || user?.email || 'U').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  {isUploading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera className="w-6 h-6 text-white" />
                  )}
                </div>
              </button>
              
              {!isEditing ? (
                <div className="flex-1">
                  <h1 className="text-2xl font-display text-white tracking-wide">
                    {profile?.display_name || user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'User'}
                  </h1>
                  <p className="text-gray-400">@{profile?.username || user?.email?.split('@')[0] || 'user'}</p>
                  {(profile?.created_at || user?.created_at) && (
                    <div className="flex items-center gap-1 text-gray-500 text-sm mt-1">
                      <Calendar className="w-3 h-3" />
                      <span>
                        Joined {new Date(profile?.created_at || user?.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex-1">
                  <h2 className="text-xl font-display text-white tracking-wide mb-1">
                    Edit Profile
                  </h2>
                  <p className="text-gray-400 text-sm">
                    Update your display name
                  </p>
                </div>
              )}
              
              {!isEditing && (
                <button
                  onClick={() => {
                    setDisplayName(profile?.display_name || '');
                    setIsEditing(true);
                    setError('');
                  }}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                >
                  <Edit2 className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Edit Form */}
          {isEditing && (
            <div className="p-4 border-t border-dive-gray">
              {error && (
                <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded-lg mb-4 text-sm">
                  {error}
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="text-gray-400 text-sm mb-1 block">Display Name</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Your name"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-sm mb-1 block">Username</label>
                  <input
                    type="text"
                    value={`@${profile?.username}`}
                    disabled
                    className="w-full opacity-50 cursor-not-allowed"
                  />
                  <p className="text-gray-500 text-xs mt-1">Username cannot be changed</p>
                </div>
                
                {/* Privacy Toggle */}
                <div className="p-4 bg-dive-darker rounded-xl border border-dive-gray">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                        <Shield className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Private Account</p>
                        <p className="text-gray-500 text-xs">Only approved followers can see your ratings</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsPrivate(!isPrivate)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        isPrivate ? 'bg-purple-500' : 'bg-dive-gray'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                          isPrivate ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setError('');
                    }}
                    className="btn-secondary flex-1 flex items-center justify-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                  >
                    {isSaving ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Stats */}
          {!isEditing && (
            <div className="grid grid-cols-4 border-t border-dive-gray">
              <Link to="/list?tab=mybars" className="py-4 px-1 text-center border-r border-dive-gray hover:bg-dive-gray/30 transition-colors">
                <div className="text-xl md:text-2xl font-bold text-dive-neon">{userRatings.length}</div>
                <div className="text-gray-500 text-xs">Ratings</div>
              </Link>
              <Link to="/connections?tab=following" className="py-4 px-1 text-center border-r border-dive-gray hover:bg-dive-gray/30 transition-colors">
                <div className="text-xl md:text-2xl font-bold text-dive-blue">{following !== undefined ? following.length : friends.length}</div>
                <div className="text-gray-500 text-xs">Following</div>
              </Link>
              <Link to="/connections?tab=followers" className="py-4 px-1 text-center border-r border-dive-gray hover:bg-dive-gray/30 transition-colors">
                <div className="text-xl md:text-2xl font-bold text-purple-400">{followers?.length || 0}</div>
                <div className="text-gray-500 text-xs">Followers</div>
              </Link>
              <Link to="/lists" className="py-4 px-1 text-center hover:bg-dive-gray/30 transition-colors">
                <div className="text-xl md:text-2xl font-bold text-amber-400">{wantToTryCount}</div>
                <div className="text-gray-500 text-xs">To Try</div>
              </Link>
            </div>
          )}
        </div>

        {/* Tilted Top Three */}
        {userRatings.length > 0 && (
          <div className="mb-6">
            <h2 className="text-white font-display text-xl tracking-wide mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-dive-amber" />
              TILTED TOP THREE
            </h2>
            <div className="space-y-2">
              {userRatings
                .sort((a, b) => b.overall - a.overall)
                .slice(0, 3)
                .map(rating => {
                  const bar = bars.find(b => b.id === rating.barId);
                  if (!bar) return null;
                  return (
                    <Link
                      key={rating.id}
                      to={`/bar/${bar.id}`}
                      className="flex items-center justify-between p-3 bg-dive-dark border border-dive-gray rounded-lg hover:border-dive-neon/50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="text-white font-medium">{bar.name}</div>
                        <div className="text-gray-500 text-sm">{bar.neighborhood}</div>
                      </div>
                      <div className="flex items-center gap-1 text-dive-amber">
                        <Star className="w-4 h-4 fill-dive-amber" />
                        <span className="font-bold">{rating.overall.toFixed(1)}</span>
                      </div>
                    </Link>
                  );
                })}
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="space-y-2 mb-6">
          <Link
            to="/lists"
            className="flex items-center justify-between p-4 bg-dive-dark border border-dive-gray rounded-xl hover:border-dive-neon/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Bookmark className="w-5 h-5 text-amber-400" />
              <div>
                <div className="text-white font-medium">My Lists</div>
                <div className="text-gray-500 text-sm">{lists.length} lists</div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-500" />
          </Link>
        </div>

        {/* Badges Section */}
        <div className="mb-6">
          <BadgeDisplay userId={user.id} />
        </div>

        {/* Referral Section */}
        <div className="mb-6">
          <ReferralShare userId={user.id} />
        </div>

        {/* Account Settings */}
        <div className="mb-6">
          <h2 className="text-white font-display text-xl tracking-wide mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-400" />
            ACCOUNT SETTINGS
          </h2>

          <div className="bg-dive-dark border border-dive-gray rounded-xl overflow-hidden">
            {/* Privacy Toggle */}
            <div className="flex items-center justify-between p-4 border-b border-dive-gray">
              <div className="flex items-center gap-3">
                {profile?.profile_privacy === 'private' ? (
                  <EyeOff className="w-5 h-5 text-purple-400" />
                ) : (
                  <Eye className="w-5 h-5 text-gray-400" />
                )}
                <div className="text-left">
                  <div className="text-white">Private Account</div>
                  <div className="text-gray-500 text-sm">
                    {profile?.profile_privacy === 'private' 
                      ? 'Only approved followers can see your activity'
                      : 'Anyone can see your ratings and follow you'
                    }
                  </div>
                </div>
              </div>
              <button
                onClick={async () => {
                  const newPrivacy = profile?.profile_privacy === 'private' ? 'public' : 'private';
                  try {
                    await updateProfile({ profile_privacy: newPrivacy });
                    setSuccess(`Account is now ${newPrivacy}`);
                    setTimeout(() => setSuccess(''), 3000);
                  } catch (err) {
                    setError(err.message || 'Failed to update privacy');
                  }
                }}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  profile?.profile_privacy === 'private' ? 'bg-purple-500' : 'bg-dive-gray'
                }`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                  profile?.profile_privacy === 'private' ? 'left-7' : 'left-1'
                }`} />
              </button>
            </div>

            {/* Email */}
            <button
              onClick={() => {
                setShowEmailChange(!showEmailChange);
                setError('');
              }}
              className="w-full flex items-center justify-between p-4 hover:bg-dive-darker transition-colors"
            >
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div className="text-left">
                  <div className="text-white">Email</div>
                  <div className="text-gray-500 text-sm">{user.email}</div>
                </div>
              </div>
              <span className="text-dive-neon text-sm">Change</span>
            </button>

            {/* Change Email Form */}
            {showEmailChange && (
              <form onSubmit={handleChangeEmail} className="p-4 border-t border-dive-gray bg-dive-darker">
                {error && (
                  <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded-lg mb-4 text-sm">
                    {error}
                  </div>
                )}
                
                <div className="space-y-4">
                  <div>
                    <label className="text-gray-400 text-sm mb-1 block">New Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="email"
                        value={emailForm.newEmail}
                        onChange={(e) => setEmailForm(prev => ({ ...prev, newEmail: e.target.value }))}
                        placeholder="new@email.com"
                        className="w-full pl-11"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-gray-400 text-sm mb-1 block">Current Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="password"
                        value={emailForm.password}
                        onChange={(e) => setEmailForm(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="••••••••"
                        className="w-full pl-11"
                        required
                      />
                    </div>
                    <p className="text-gray-500 text-xs mt-1">Enter your password to confirm the change</p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowEmailChange(false);
                        setEmailForm({ newEmail: '', password: '' });
                        setError('');
                      }}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSaving}
                      className="btn-primary flex-1"
                    >
                      {isSaving ? 'Sending...' : 'Send Verification'}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* User's Rated Bars */}
        {ratedBars.length > 0 && (
          <div className="mb-6">
            <h2 className="text-white font-display text-xl tracking-wide mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-dive-amber" />
              YOUR RATINGS
            </h2>
            <div className="space-y-3">
              {ratedBars.slice(0, 5).map(({ bar, overall, isDiveBar }) => (
                <Link
                  key={bar.id}
                  to={`/bar/${bar.id}`}
                  className="block bg-dive-dark border border-dive-gray rounded-lg p-4 hover:border-dive-neon/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-white font-medium">{bar.name}</h3>
                      <div className="flex items-center gap-1 text-gray-500 text-sm">
                        <MapPin className="w-3 h-3" />
                        <span>{bar.neighborhood}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <StarRating rating={overall} size="sm" />
                      <div className="text-xs text-gray-500 mt-1">
                        {isDiveBar ? '🍺 Dive' : '🍸 Not Dive'}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
              {ratedBars.length > 5 && (
                <p className="text-center text-gray-500 text-sm">
                  + {ratedBars.length - 5} more ratings
                </p>
              )}
            </div>
          </div>
        )}

        {/* Empty State */}
        {ratedBars.length === 0 && (
          <div className="text-center py-8 bg-dive-dark border border-dive-gray rounded-xl mb-6">
            <Star className="w-12 h-12 text-dive-gray mx-auto mb-3" />
            <h3 className="text-white mb-2">No ratings yet</h3>
            <p className="text-gray-500 text-sm mb-4">
              Start exploring and rating dive bars!
            </p>
            <Link to="/list" className="btn-primary inline-flex items-center gap-2">
              <img src="/logo-icon.png" alt="" className="w-4 h-4 object-contain" />
              Browse Bars
            </Link>
          </div>
        )}

        {/* Sign Out Button */}
        {/* Troubleshooting Section */}
        <div className="bg-dive-dark border border-dive-gray rounded-xl p-4 mb-6">
          <h3 className="text-white font-medium flex items-center gap-2 mb-3">
            <Wrench className="w-4 h-4 text-gray-400" />
            Troubleshooting
          </h3>
          <p className="text-gray-400 text-sm mb-3">
            If you're having issues (data not loading, wrong username, etc.), try clearing the app cache.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => {
                if (confirm('This will clear cached data and reload the app. You\'ll stay logged in. Continue?')) {
                  clearAppCache();
                }
              }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/50 rounded-lg text-amber-400 hover:bg-amber-500/20 hover:border-amber-500 transition-colors text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Clear App Cache
            </button>
          </div>
          <p className="text-gray-600 text-xs mt-3">
            App Version: {APP_VERSION}
          </p>
        </div>

        <button
          onClick={handleSignOut}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-dive-dark border border-dive-gray rounded-lg text-gray-400 hover:text-white hover:border-gray-500/50 transition-colors mb-4"
        >
          <LogOut className="w-5 h-5" />
          Sign Out
        </button>

        {/* Delete Account */}
        <button
          onClick={() => setShowDeleteModal(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 hover:bg-red-500/20 hover:border-red-500 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Delete Your Account
        </button>

        {/* Footer 21+ warning */}
        <div className="text-center mt-6 space-y-2">
          <p className="text-gray-600 text-xs">
            Tilted is for users 21 years of age or older. Please drink responsibly.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/privacy" className="text-gray-500 text-xs hover:text-dive-neon transition-colors">
              Privacy Policy
            </Link>
            <span className="text-gray-600">•</span>
            <a 
              href="https://forms.gle/xt1xVnc9X8wLWKqt7" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-500 text-xs hover:text-dive-neon transition-colors"
            >
              Report Bug / Suggest Feature
            </a>
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-dive-dark border border-red-500/50 rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-white text-lg font-medium">Delete Account</h3>
                <p className="text-gray-500 text-sm">This action cannot be undone</p>
              </div>
            </div>

            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
              <p className="text-red-300 text-sm">
                This will permanently delete your account, including:
              </p>
              <ul className="text-red-300/80 text-sm mt-2 ml-4 list-disc space-y-1">
                <li>Your profile and settings</li>
                <li>All your ratings and reviews</li>
                <li>Your comments</li>
                <li>Your custom lists</li>
              </ul>
            </div>

            {deleteError && (
              <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded-lg mb-4 text-sm">
                {deleteError}
              </div>
            )}

            <div className="mb-4">
              <label className="text-gray-400 text-sm mb-1 block">Enter your password to confirm</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-11"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletePassword('');
                  setDeleteError('');
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {isDeleting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
